import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';
import { FormattedMessage, useIntl } from 'react-intl';

import {
    Form,
    FormValue,
    getTermsURL,
    getPrivacyPolicyURL,
    getLanguageFromLocale,
    PrimaryButton,
    QuestionCompact,
    QuestionInputTypes,
    Sizes,
} from '@ratehub/base-ui';

import UserInfoCCPropTypes from '../definitions/UserInfoCCPropTypes';
import submitLeadConversion from '../functions/submitLeadConversion';


function CreditCardCTAModal({
    cardInfo,
    onAcceptOrDecline,
    userInfo,
}) {
    const intl = useIntl();

    const termsLink = getTermsURL(intl.locale);
    const privacyLink = getPrivacyPolicyURL(intl.locale);

    function handleCTASubmit({ email, phone, fullName }) {
        // need lastNameStrings for cases where there are multiple ' ' after last name
        // e.g. 'Bruce Lee Wayne' -> firstName: 'Bruce', lastName: 'Lee Wayne'
        const [ firstName = '', ...lastNameStrings ] = fullName?.split(' ') ?? [];
        const lastName = lastNameStrings.join(' ');

        submitLeadConversion(cardInfo, {
            email,
            language: getLanguageFromLocale(intl.locale),
            creditScore: userInfo?.creditScore,
            income: userInfo?.income,
            phone,
            firstName,
            lastName,
        });

        onAcceptOrDecline();
    }


    return (
        <MyModalContainer className="rh-mx-auto rh-p-1_5 rh-b-1px-solid-stone rh-mb2">
            <div className="rh-display-flex rh-align-items-center rh-p-1 rh-bt-1px-solid-stone rh-bg-stone-light">
                <img
                    src={cardInfo.cardImageURL}
                    alt=""
                    width="70"
                />
                <div className="MyTitleWrappingFlexItem">
                    <p className="rh-text-xs rh-mt-0 rh-mr-0 rh-mb-0_125 rh-ml-1">
                        <FormattedMessage
                            id="edb.cc.cta-modal-gate.applying-for"
                            defaultMessage="You’re applying for"
                        />
                    </p>
                    <h3 className="rh-title-3xs rh-my-0 rh-ml-1">
                        {cardInfo.name}
                    </h3>
                </div>
            </div>

            <Form onSubmit={handleCTASubmit}>
                <QuestionCompact
                    className="question-container"
                    field={{
                        name: 'fullName',
                        type: 'string',
                    }}
                    labelMessage={(
                        <FormattedMessage
                            id="edb.cc.cta-modal-gate.label.name"
                            defaultMessage="Your name"
                        />
                    )}
                />
                <QuestionCompact
                    className="question-container"
                    field={{
                        name: 'email',
                        type: QuestionInputTypes.EMAIL,
                    }}
                    labelMessage={(
                        <FormattedMessage
                            id="edb.cc.cta-modal-gate.label.email"
                            defaultMessage="Your email address"
                        />
                    )}
                />
                <QuestionCompact
                    className="question-container"
                    field={{
                        name: 'phone',
                        type: QuestionInputTypes.PHONE,
                    }}
                    labelMessage={(
                        <FormattedMessage
                            id="edb.cc.cta-modal-gate.label.phone"
                            defaultMessage="Your phone number"
                        />
                    )}
                />

                <FormValue>
                    {({ email, fullName, phone }) => (
                        <PrimaryButton
                            data-name="cta-modal-submit"
                            size="small"
                            message={(
                                <FormattedMessage
                                    id="edb.cc.cta-modal-gate.CTA"
                                    defaultMessage="submit"
                                />
                            )}
                            disabled={!email || !fullName || !phone}
                        />
                    )}
                </FormValue>
            </Form>

            <p className="rh-text-s disclaimer">
                <FormattedMessage
                    id="edb.cc.cta-modal-gate.disclaimer"
                    defaultMessage="By clicking above, you agree to Ratehub.ca’s <privacy>Privacy Policy</privacy> and <terms>Terms and Conditions</terms>"
                    values={{
                        terms: msg => <a href={termsLink} target="_blank" rel="noopener">{msg}</a>, // eslint-disable-line
                        privacy: msg => <a href={privacyLink} target="_blank" rel="noopener">{msg}</a>, // eslint-disable-line
                    }}
                />
            </p>
        </MyModalContainer>
    );
}

CreditCardCTAModal.propTypes = {
    cardInfo: PropTypes.shape({
        cardImageURL: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        detailsURL: PropTypes.string.isRequired,
        bank: PropTypes.string.isRequired,
        isMonetized: PropTypes.bool.isRequired,
        isSponsored: PropTypes.bool.isRequired,
    }).isRequired,

    onAcceptOrDecline: PropTypes.func.isRequired,
    userInfo: UserInfoCCPropTypes,
};

CreditCardCTAModal.defaultProps = {
    userInfo: undefined,
};

const MyModalContainer = styled.div`
    max-width: 32.5em;
    box-sizing: border-box;

    .question-container {
        margin-top: ${Sizes.SPACING.TWO_AND_A_HALF};
        margin-bottom: ${Sizes.SPACING.TWO_AND_A_HALF};
    }

    .disclaimer {
        margin-bottom: 0;
    }
`;


export default observer(CreditCardCTAModal);
